import classnames from 'classnames';
import type React from 'react';
import { forwardRef, useCallback, useEffect } from 'react';

import { AccordionItem } from '@/core/tamagoshi/Accordion/AccordionItem';

import { useFiltersPanel } from '../../../FiltersPanel/FiltersPanel';
import { SeeMore } from '../SeeMore/SeeMore';

import styles from './FilterBlock.module.scss';

interface Props extends React.PropsWithChildren {
  title: string;
  id: string;
  className?: string;
  disabled?: boolean;
  expandable?: boolean;
  expanded?: boolean;
  onExpandedChange?(value: boolean): void;
  ref?: React.Ref<HTMLDivElement>;
}

export const FilterBlock = forwardRef<HTMLDivElement, Props>(
  (
    {
      className,
      id,
      title,
      disabled = false,
      children,
      expandable = false,
      expanded = false,
      onExpandedChange,
    },
    ref,
  ) => {
    const [open, onToggle] = useFiltersPanel(id);

    const handleToggle = useCallback(() => {
      if (onToggle) {
        onToggle(undefined as any, id, false);
      }
    }, [id, onToggle]);

    useEffect(() => {
      if (open && disabled && onToggle) {
        handleToggle();
      }
    }, [disabled, open, onToggle, id, handleToggle]);

    const handleSeeMoreClick = () => {
      if (onExpandedChange) {
        onExpandedChange(!expanded);
      }
    };

    return (
      <>
        {ref && <div ref={ref} />}

        <AccordionItem
          className={classnames(styles.root, className)}
          headerClassName={styles.header}
          contentClassName={styles.content}
          bodyClassName={styles.body}
          id={id}
          open={open}
          onToggle={onToggle}
          title={title}
          disabled={disabled}
        >
          {children}
          {expandable && (
            <SeeMore
              className={styles.seeMore}
              onClick={handleSeeMoreClick}
              active={expanded}
            />
          )}
        </AccordionItem>
      </>
    );
  },
);

FilterBlock.displayName = 'FilterBlock';
